import { useCallback, useEffect, useRef } from "react";
import { subscribe, EVENTS } from "@/lib/services/ws.service";
import { useNotificationStore } from "@/store";
import { toast } from "sonner";
import i18next from "i18next";
import { mapNotificationRow } from "@/lib/notifications/map-notification-row.js";
import { resolveLocalizedNotificationCopy } from "@/lib/notifications/resolve-localized-notification-copy.js";
import { notificationIntegration } from "@/lib/api/client.js";

const POLL_INTERVAL_MS = 60_000;
const VISIBILITY_STALE_MS = 60_000;

async function localizeNotificationToastCopy(mapped) {
  const language = i18next.resolvedLanguage || i18next.language;
  try {
    await i18next.loadNamespaces("notificationEvents");
  } catch {
    // Fall back to stored delivery strings if catalog cannot load.
  }
  return resolveLocalizedNotificationCopy(mapped, {
    title: mapped.title,
    message: mapped.message,
    language,
  });
}

export function useNotificationWebSocket() {
  const addNotification = useNotificationStore((s) => s.addNotification);
  const lastFetchAtRef = useRef(0);
  const pollRef = useRef(null);
  const firstConnectRef = useRef(true);

  const refetchInbox = useCallback(async () => {
    lastFetchAtRef.current = Date.now();
    await notificationIntegration.fetchNotifications(1, false);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribe(EVENTS.NOTIFICATION_RECEIVED, (payload) => {
      if (payload && typeof payload === "object") {
        const data = payload.data || {};
        const baseNotif = {
          id:
            payload.id ??
            payload.result?.notificationId ??
            String(Date.now()),
          title: payload.title ?? "New Notification",
          message: payload.body ?? payload.message ?? "",
          createdAt: payload.createdAt ?? new Date().toISOString(),
          isRead: payload.isRead ?? false,
          type: payload.type,
          actionUrl: payload.actionUrl,
          data,
          params: {
            ...(data.i18n?.params || {}),
            i18n: data.i18n,
            pageKey: data.pageKey,
            entityId: data.entityId,
            category: data.category,
            navigationKind: data.navigationKind,
          },
        };

        addNotification(baseNotif);
        const mapped = mapNotificationRow({
          ...baseNotif,
          _id: baseNotif.id,
          body: baseNotif.message,
          read: baseNotif.isRead,
        });

        void localizeNotificationToastCopy(mapped).then(({ title, message }) => {
          if (baseNotif.type === 'ALERT' || baseNotif.type === 'error') {
            toast.error(title, { description: message });
          } else if (baseNotif.type === 'SUCCESS' || baseNotif.type === 'success') {
            toast.success(title, { description: message });
          } else {
            toast.info(title, { description: message });
          }
        });
      }
    });

    function startPolling() {
      if (pollRef.current) return;
      pollRef.current = setInterval(() => {
        refetchInbox();
      }, POLL_INTERVAL_MS);
    }

    function stopPolling() {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }

    const unsubDisconnect = subscribe('disconnect', () => {
      startPolling();
    });

    const unsubConnect = subscribe('connect', () => {
      stopPolling();
      if (firstConnectRef.current) {
        firstConnectRef.current = false;
        return;
      }
      refetchInbox();
    });

    function onVisibility() {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchAtRef.current < VISIBILITY_STALE_MS) return;
      refetchInbox();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      unsubscribe();
      unsubDisconnect();
      unsubConnect();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [addNotification, refetchInbox]);
}
